import { useMemo } from "react";
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  BellRing,
  ChartColumnBig,
  Gauge,
} from "lucide-react";
import {
  getKeyTrendScoreBehaviorsForDisplayWithDetails,
  KEY_TREND_SCORE_MAX,
} from "@/components/root-cause-analysis/rca-key-trend-score.js";
import { computeTrendDrift } from "@/components/root-cause-analysis/pre-shutdown-drift-summary.jsx";
import { cn } from "@/lib/utils.js";

const DRIFT_DISPLAY_OPTIONS = {
  firstWindowPct: 100 / 3,
  lastWindowHours: 2,
  shiftThresholdPct: 15,
};

/**
 * @param {{ behaviorId: string, driftUp?: boolean }} props
 */
function RcaTrendScoreBehaviorIcon({ behaviorId, driftUp = true }) {
  const iconClass = "h-3 w-3 shrink-0";

  switch (behaviorId) {
    case "thresholdBreach":
      return (
        <BellRing
          className={cn(iconClass, "text-amber-600 dark:text-amber-400")}
          aria-hidden
        />
      );
    case "drift": {
      const Arrow = driftUp ? ArrowUpRight : ArrowDownRight;
      return (
        <Arrow
          className={cn(
            iconClass,
            driftUp
              ? "text-emerald-600 dark:text-emerald-400"
              : "text-red-600 dark:text-red-400"
          )}
          aria-hidden
        />
      );
    }
    case "stepChange":
      return (
        <ChartColumnBig
          className={cn(iconClass, "text-foreground")}
          aria-hidden
        />
      );
    case "thresholdApproach":
      return (
        <AlertTriangle
          className={cn(iconClass, "text-amber-600 dark:text-amber-400")}
          aria-hidden
        />
      );
    case "zScore":
      return <Gauge className={cn(iconClass, "text-foreground")} aria-hidden />;
    default:
      return null;
  }
}

/**
 * @param {{
 *   behavior: import("@/components/root-cause-analysis/rca-key-trend-score.js").KeyTrendScoreBehaviorDisplay,
 *   driftUp?: boolean,
 * }} props
 */
/** Reserved height for four score pills so cards align. */
const SCORE_PILLS_MIN_HEIGHT = "4.75rem";

function RcaTrendScoreBehaviorPill({ behavior, driftUp }) {
  const earned = Number(behavior.earned) || 0;
  const applies = earned > 0;

  return (
    <li
      aria-hidden={!applies}
      className={cn(
        "flex min-h-[1.125rem] items-center gap-1 rounded-md border border-border/90 bg-muted px-1.5 py-0.5 text-foreground",
        !applies && "invisible pointer-events-none"
      )}>
      <RcaTrendScoreBehaviorIcon behaviorId={behavior.id} driftUp={driftUp} />
      <p className='m-0 min-w-0 flex-1 leading-tight'>
        <span className='text-[10px] font-semibold text-foreground'>
          {behavior.label}
        </span>
        {behavior.detail ? (
          <span className='text-[10px] font-normal text-muted-foreground'>
            {behavior.detail}
          </span>
        ) : null}
      </p>
      <span className='shrink-0 text-[10px] font-semibold tabular-nums text-foreground'>
        +{earned}
      </span>
    </li>
  );
}

/**
 * Left column of a trend card: tag name, total score out of 100, and per-behavior pills.
 *
 * @param {{
 *   row: import("@/components/root-cause-analysis/rca-measurement-trend-shared.jsx").MeasurementWithStatsRow|Record<string, unknown>,
 *   lineColor: string,
 *   label: import("react").ReactNode,
 *   uom: string,
 *   score: import("@/components/root-cause-analysis/rca-key-trend-score.js").KeyTrendScoreBreakdown,
 *   useAlarmLimits?: boolean,
 * }} props
 */
export function RcaTrendScoreSidebar({
  row,
  lineColor,
  label,
  uom,
  score,
  useAlarmLimits = false,
}) {
  const total = Number(score?.total) || 0;
  const behaviors = useMemo(() => {
    const list = getKeyTrendScoreBehaviorsForDisplayWithDetails(
      score,
      row,
      useAlarmLimits,
      DRIFT_DISPLAY_OPTIONS
    );
    const visible = [];
    const hidden = [];
    for (const behavior of list) {
      if ((Number(behavior.earned) || 0) > 0) {
        visible.push(behavior);
      } else {
        hidden.push(behavior);
      }
    }
    return [...visible, ...hidden];
  }, [score, row, useAlarmLimits]);
  const driftUp = useMemo(() => {
    const { up } = computeTrendDrift(row, DRIFT_DISPLAY_OPTIONS);
    return up === true;
  }, [row]);

  return (
    <div className='flex min-w-0 flex-col gap-1'>
      <div className='min-w-0'>
        <div className='flex min-w-0 items-start gap-1.5'>
          <span
            className='mt-0.5 h-2 w-2 shrink-0 rounded-full'
            style={{ backgroundColor: lineColor }}
            aria-hidden
          />
          <span className='min-w-0 break-words text-xs font-medium leading-snug text-foreground'>
            {label}
            {uom ? (
              <span className='font-light text-muted-foreground'> ({uom})</span>
            ) : null}
          </span>
        </div>
        <p className='mt-0.5 text-xs font-semibold tabular-nums leading-tight text-foreground'>
          {total}{" "}
          <span className='font-normal text-muted-foreground'>
            / {KEY_TREND_SCORE_MAX}
          </span>
        </p>
      </div>
      <ul
        className='flex list-none flex-col gap-0.5 p-0'
        style={{ minHeight: SCORE_PILLS_MIN_HEIGHT }}
        aria-label='Score breakdown'>
        {behaviors.map((behavior) => (
          <RcaTrendScoreBehaviorPill
            key={behavior.id}
            behavior={behavior}
            driftUp={driftUp}
          />
        ))}
      </ul>
    </div>
  );
}
